import type { ReactElement } from "react";
import { Stack, Surface, Text, ValueUnavailable } from "../ui-kit";

interface MetricWidgetCommonProps {
  readonly title: string;
  /**
   * The title's heading level — pick it for correct document nesting under
   * whatever section heading (or none) precedes this widget in a given
   * caller. Defaults to `h3`, matching the dashboard's own h1 → h2 → h3
   * nesting (`DashboardView` → "Product metrics" → each widget).
   */
  readonly titleAs?: "h2" | "h3";
}

interface MetricWidgetValueProps extends MetricWidgetCommonProps {
  /** A real, already-formatted metric value (e.g. a count). */
  readonly value: string;
}

interface MetricWidgetUnavailableProps extends MetricWidgetCommonProps {
  /** Plain-language reason the metric has no value yet — required, same discipline as `ValueUnavailable` (never an unexplained blank). */
  readonly unavailableReason: string;
}

/**
 * A widget renders either a real value or an honest "not yet available"
 * reason — never both, never neither (ADR-052 §3 honest-absence discipline).
 * Distinguished structurally (`"value" in props`) rather than by a discriminant
 * field, so every existing not-yet-available caller keeps its two-prop call
 * shape unchanged.
 */
export type MetricWidgetProps = MetricWidgetValueProps | MetricWidgetUnavailableProps;

/**
 * Shared metric-card presentation (ISSUE-352 AC2/AC3/AC4, extended by
 * ISSUE-455 Deliverable 3 to also render a real value). Activation,
 * Retention (Day 7), and Core engagement still render the not-yet-available
 * state until their backend endpoint ships — never a fabricated or
 * zero-filled number standing in for missing data (AC4). `UsersView` is the
 * first caller to pass a real `value`.
 */
export function MetricWidget(props: MetricWidgetProps): ReactElement {
  return (
    <Surface as="section" aria-label={props.title} padding="default">
      <Stack gap={2}>
        <Text as={props.titleAs ?? "h3"} variant="heading">
          {props.title}
        </Text>
        {"value" in props ? (
          <Text as="p" variant="display">
            {props.value}
          </Text>
        ) : (
          <ValueUnavailable label="Not yet available" reason={props.unavailableReason} />
        )}
      </Stack>
    </Surface>
  );
}
